// Copyright 2016 Sorama B.V.

import { autoinject, computedFrom, Disposable } from "aurelia-framework";
import { HttpRequestMessage, HttpResponseMessage } from "aurelia-http-client";
import { AcquisitionServiceSettings, StorageInfo, VersionInfo } from "datatypes/SoramaLocalApi";
import { ClientVersionError } from "errors/ClientVersionError";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { StringExtensions } from "extensions/StringExtensions";
import { parseUri } from "extensions/UriExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler, IWebRequestInterceptorHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ClientStatus, FreeSpaceStatus, HardwareType, IClientService } from "services/Client/IClientService";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";

@autoinject()
export class ClientService implements IClientService, ILocalApi, IWebRequestInterceptorHandler {
    //#region Injected Services
    private readonly webRequestHandler: IWebRequestHandler;
    private readonly notificationService: INotificationService;
    private readonly userSettingsService: IUserSettingsService;
    //#endregion

    //#region Constants
    private static readonly RefreshIntervalMs: number = 60000; // [ms]
    private static readonly RescanIntervalMs: number = 30000;
    private static readonly possiblePorts: number[] = [9000, 25103, 28010, 7301, 6800];
    private static readonly address: string = "https://local.sorama.eu:{0}";
    private static readonly baseUriFormat: string = "{0}/api";
    private static readonly commonFormat: string = "{0}/Common";
    private static readonly frontEndsFormat: string = "{0}/FrontEnds";
    private static readonly captureDevicesFormat: string = "{0}/CaptureDevices";
    private static readonly streamFormat: string = "{0}/Stream";
    private static readonly storageFormat: string = "{0}/Storage";
    private static readonly videoFileFormat: string = "{0}/VideoFile";
    private static readonly farfieldFormat: string = "{0}/Farfield";
    private static readonly soundProcessingFormat: string = "{0}/SoundProcessing";
    private static readonly analysisFormat: string = "{0}/Analysis";
    private static readonly motionFormat: string = "{0}/Motion";
    private static readonly camIV64Format: string = "{0}/CamIV64";

    private static readonly localStorageKey: string = "ILocalApi";
    private static readonly servicePortKey: string = "ServicePort";

    private static readonly versionCall: string = "/Version";
    private static readonly storageCall: string = "/Storage";
    private static readonly settingsCall: string = "/Settings";
    private static readonly folderPathCall: string = "/FolderPath";
    private static readonly firmwarePath: string = "/AvailableFirmwareVersions";
    private static readonly openFirmwareWindowCall: string = "/FirmwareUpdate";
    private static readonly freeSpaceLimit: number = 10737418240;
    private static versionXmlUrl = "/Sorama%20Acquisition%20Service.json";

    //#endregion

    //#region Properties
    private _status: ClientStatus = ClientStatus.NotAvailable;
    private _storage: StorageInfo;
    private _settings: AcquisitionServiceSettings;
    private _freeSpaceStatus: FreeSpaceStatus = FreeSpaceStatus.EnoughFreeSpace;

    private _storageError: string;
    private _settingsError: string;

    @computedFrom("_status")
    public get status(): ClientStatus {
        return this._status;
    }

    @computedFrom("_freeSpaceStatus")
    public get freeSpaceStatus(): FreeSpaceStatus {
        return this._freeSpaceStatus;
    }

    @computedFrom("_storage")
    public get storageStatus(): StorageInfo {
        return this._storage;
    }

    @computedFrom("_settings")
    public get settings(): AcquisitionServiceSettings {
        return this._settings;
    }

    @computedFrom("_settings")
    public get storagePath(): string {
        return this._settings ? this._settings.storagePath : undefined;
    }

    @computedFrom("_storageError")
    public get storageError(): string {
        return this._storageError;
    }

    @computedFrom("_settingsError")
    public get settingsError(): string {
        return this._settingsError;
    }

    public get defaultBaseAddress(): string {
        return this.getBaseAddress(ClientService.possiblePorts[0]);
    }

    public get baseAddress(): Promise<string> {
        const portPromise =
            this.portPromise === undefined
                ? this.port === undefined
                    ? Promise.reject<number>(undefined)
                    : Promise.resolve(this.port)
                : this.portPromise;

        return portPromise.then(this.getBaseAddress).catch((e) => {
            if (this.port === undefined) {
                // Check when we last did a check, we dont want to spam too much
                if (
                    this.lastScanTime !== undefined &&
                    new Date().getTime() - this.lastScanTime < ClientService.RescanIntervalMs
                ) {
                    throw e;
                }

                // We have not yet found a port, retry to get the port
                this.scanPorts();
                return this.portPromise.then(this.getBaseAddress);
            } else {
                // We have a port but we are unable to connect for some reason, just throw
                throw e;
            }
        });
    }

    public get baseUri(): Promise<string> {
        return this.baseAddress.then((baseAddress) => {
            return StringExtensions.format(ClientService.baseUriFormat, baseAddress);
        });
    }

    public get common(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.commonFormat, baseUri);
        });
    }

    public get frontEnds(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.frontEndsFormat, baseUri);
        });
    }

    public get captureDevices(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.captureDevicesFormat, baseUri);
        });
    }

    public get stream(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.streamFormat, baseUri);
        });
    }

    public get storage(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.storageFormat, baseUri);
        });
    }

    public get videoFile(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.videoFileFormat, baseUri);
        });
    }

    public get farfield(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.farfieldFormat, baseUri);
        });
    }

    public get soundProcessing(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.soundProcessingFormat, baseUri);
        });
    }

    public get analysis(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.analysisFormat, baseUri);
        });
    }

    public get motion(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.motionFormat, baseUri);
        });
    }

    public get camIV64(): Promise<string> {
        return this.baseUri.then((baseUri) => {
            return StringExtensions.format(ClientService.camIV64Format, baseUri);
        });
    }

    //#endregion

    //#region Fields
    private port: number;
    private portPromise: Promise<number>;
    private lastScanTime: number;
    private interceptorDisposable: Disposable;
    private refreshIntervalHandle: number;

    private updateStoragePromise: Promise<StorageInfo>;
    private cachedRemoteVersion: Promise<VersionInfo>;
    //#endregion

    constructor(
        webRequestHandler: IWebRequestHandler,
        notificationService: INotificationService,
        userSettingsService: IUserSettingsService
    ) {
        this.webRequestHandler = webRequestHandler;
        this.notificationService = notificationService;
        this.userSettingsService = userSettingsService;

        this.refreshIntervalHandle = window.setInterval(() => {
            this.updateServiceAvailability().then(() => {
                if (this._status !== ClientStatus.NotAvailable && this._status !== ClientStatus.UnsupportedVersion) {
                    this.updateStorage().catch(() => {
                        /* Ignore any errors in our periodic update */
                    });
                    this.updateSettings().catch(() => {
                        /* Ignore any errors in our periodic update */
                    });
                }
            });
        }, ClientService.RefreshIntervalMs);

        this.updateServiceAvailability();
    }

    //#region ILocalApi

    private scanPorts(): void {
        this.lastScanTime = new Date().getTime();

        // First check if we have a cached port id
        this.portPromise = this.userSettingsService
            .get(ClientService.localStorageKey, ClientService.servicePortKey)
            // If we do not have a cached port number, take the first one as first guess
            .catch(() => ClientService.possiblePorts[0])
            .then((portString) => {
                const port = Number(portString);
                return this.testAcquisitionServiceAtPort(port).then(() => {
                    return port;
                });
            })
            .catch(() => {
                // No known port, scan all of them
                const portPromises = ClientService.possiblePorts.map((port, i) => {
                    // Try to connect to the acquisition service using the provided port
                    return this.testAcquisitionServiceAtPort(port)
                        .then(() => ({ port: port, index: i }))
                        .catch<{ port: number; index: number }>(() => ({ port: undefined, index: i }));
                });

                // Find the first ac to respond
                const onRaceComplete = (x: { port: number; index: number }): Promise<number> => {
                    if (x.port === undefined) {
                        portPromises[x.index] = undefined;

                        if (portPromises.length === 0 || portPromises.every((x) => !x)) {
                            return Promise.reject<number>(new Error("Cannot connect to the Acquisition Service"));
                        }

                        return Promise.race(portPromises.filter((x) => x)).then(onRaceComplete);
                    }

                    return Promise.resolve(x.port);
                };

                return Promise.race(portPromises).then(onRaceComplete);
            })
            .then((port) => {
                // We found a port, cache it
                this.port = port;
                this.userSettingsService.set(
                    ClientService.localStorageKey,
                    ClientService.servicePortKey,
                    this.port.toFixed(0)
                );

                const baseAddress = StringExtensions.format(ClientService.address, this.port.toFixed(0));
                const uri = parseUri(baseAddress);

                if (this.interceptorDisposable) {
                    this.interceptorDisposable.dispose();
                    this.interceptorDisposable = undefined;
                }

                // Subscribe this class as interceptor for the webrequest handler
                this.interceptorDisposable = this.webRequestHandler.addInterceptor(uri.host, this);

                return port;
            });
    }

    private testAcquisitionServiceAtPort(port: number): Promise<void> {
        return this.webRequestHandler
            .createGetRequest<string>(this.getBaseAddress(port))
            .send()
            .then((response) => {
                /* Successful connection, check response */
                if (response.isSuccess && response.response.indexOf("Sorama") !== -1) {
                    return;
                }
                throw new Error("Cannot connect to the Acquisition Service");
            });
    }

    private getBaseAddress = (port: number): string => {
        return StringExtensions.format(ClientService.address, port.toFixed(0));
    };

    //#endregion

    //#region IWebRequestInterceptorHandler Members
    public onSendRequest(request: HttpRequestMessage): Promise<void> {
        if (request.url.endsWith(ClientService.versionCall)) {
            // Always allow this call
            return Promise.resolve();
        }

        if (this.status === ClientStatus.NotAvailable) {
            // Also update status
            this.updateStatus();
            return Promise.resolve();
        }

        if (this._status === ClientStatus.UnsupportedVersion)
            return Promise.reject(new ClientVersionError("Unsupported acquisition service version"));
    }

    public onReceiveResponse(response: HttpResponseMessage): Promise<void> {
        return Promise.resolve();
    }

    public onResponseError(request: HttpResponseMessage): Promise<boolean> {
        if (request.statusCode === 0 && this.status === ClientStatus.Available) {
            // Do status update
            this.updateStatus();
        }

        return Promise.resolve(false);
    }
    //#endregion

    //#region IClientService Members
    public updateStatus(): Promise<ClientStatus> {
        return this.updateServiceAvailability().then(() => this.status);
    }

    public updateStorage(forceUpdate = false): Promise<StorageInfo> {
        if (this.updateStoragePromise && !forceUpdate) return this.updateStoragePromise;

        this._storageError = null;

        const existingPromise = !this.updateStoragePromise
            ? Promise.resolve<StorageInfo>(undefined)
            : this.updateStoragePromise;

        this.updateStoragePromise = existingPromise
            .then(() => this.common)
            .then((uri) => this.webRequestHandler.createGetRequest<StorageInfo>(uri + ClientService.storageCall).send())
            .then((x) => {
                this._storage = x.content;
                this.updateStoragePromise = null;
                this.freeSpaceCheck();
                return this._storage;
            })
            .catch((e: HttpResponseMessageError) => {
                this._storageError = e.message;
                this.updateStoragePromise = null;
                throw e;
            });

        return this.updateStoragePromise;
    }

    public updateSettings(settings?: AcquisitionServiceSettings): Promise<AcquisitionServiceSettings> {
        this._storageError = null;

        if (!settings) {
            return this.common.then((uri) => {
                return this.webRequestHandler
                    .createGetRequest<AcquisitionServiceSettings>(uri + ClientService.settingsCall)
                    .send()
                    .then((x) => (this._settings = x.content))
                    .catch((e: HttpResponseMessageError) => {
                        this._settingsError = e.message;
                        throw e;
                    });
            });
        }

        return this.common.then((uri) => {
            // Update settings
            return this.webRequestHandler
                .createPutRequest<AcquisitionServiceSettings>(uri + ClientService.settingsCall, settings)
                .send()
                .then((x) => {
                    this._settings = settings;
                    this.updateStorage(true);
                    return this._settings;
                })
                .catch((e: HttpResponseMessageError) => {
                    this._settingsError = e.message;
                    throw e;
                });
        });
    }

    public getInstalledVersion(): Promise<VersionInfo> {
        return this.common.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<VersionInfo>(uri + ClientService.versionCall)
                .send()
                .then((x) => (typeof x.content === "string" ? JSON.parse(<string>x.content) : x.content));
        });
    }

    public getRemoteVersion(): Promise<VersionInfo> {
        if (!this.cachedRemoteVersion) {
            this.cachedRemoteVersion = this.webRequestHandler
                .createGetRequest<VersionInfo>(ClientService.versionXmlUrl)
                .send()
                .then((x) => (typeof x.content === "string" ? JSON.parse(<string>x.content) : x.content));
        }
        return this.cachedRemoteVersion;
    }

    public isUpdateAvailable(): Promise<boolean> {
        return this.updateServiceAvailability().then(() => {
            return this._status == ClientStatus.OutdatedVersion || this._status == ClientStatus.UnsupportedVersion;
        });
    }

    public isUpdateRequired(): Promise<boolean> {
        return this.updateServiceAvailability().then(() => {
            return this._status == ClientStatus.UnsupportedVersion;
        });
    }

    public getFolderPath(): Promise<string> {
        return this.common.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<string>(uri + ClientService.folderPathCall)
                .send()
                .then((x) => x.content);
        });
    }

    public getAvailableFirmwareVersion(hardwareType: HardwareType): Promise<VersionInfo> {
        return this.common.then((uri) => {
            return this.webRequestHandler
                .createGetRequest(uri + ClientService.firmwarePath + "/" + hardwareType)
                .send()
                .then((x) => <VersionInfo>x.content);
        });
    }

    public openFirmwareUpdateWindow(): Promise<void> {
        return this.common.then((uri) => {
            return this.webRequestHandler
                .createGetRequest(uri + ClientService.openFirmwareWindowCall)
                .send()
                .then(() => {});
        });
    }

    //#endregion

    //#region Helper Methods
    private updateServiceAvailability(): Promise<void> {
        // When update service availability reset the port scan timeout
        this.lastScanTime = undefined;
        return this.getInstalledVersion()
            .then((installedVersion) => {
                return this.getRemoteVersion()
                    .then((remoteVersion) => {
                        if (installedVersion.Version === remoteVersion.Version) {
                            this._status = ClientStatus.Available;
                            return;
                        }

                        // this parses major minor patch version
                        let regex = new RegExp("^(\\d+)\\.(\\d+)\\.(\\d+)$");

                        // Unknown format and different, so update required
                        if (!installedVersion.Version.match(regex) || !remoteVersion.Version.match(regex)) {
                            this._status = remoteVersion.IsOptional
                                ? ClientStatus.OutdatedVersion
                                : ClientStatus.UnsupportedVersion;
                            return;
                        }

                        // Otherwise, use the new format
                        regex = new RegExp("(\\d+)", "g");
                        const installedVersionNumbers = installedVersion.Version.match(regex).map((y: unknown) =>
                            Number(y)
                        );
                        const remoteVersionNumbers = remoteVersion.Version.match(regex).map((y: unknown) => Number(y));
                        if (
                            remoteVersionNumbers[0] > installedVersionNumbers[0] ||
                            (remoteVersionNumbers[0] <= installedVersionNumbers[0] &&
                                remoteVersionNumbers[1] > installedVersionNumbers[1])
                        ) {
                            this._status = ClientStatus.UnsupportedVersion;
                            return;
                        } else if (remoteVersionNumbers[2] > installedVersionNumbers[2]) {
                            this._status = ClientStatus.OutdatedVersion;
                            return;
                        }

                        // Version number is higher, so its ok
                        this._status = ClientStatus.Available;
                    })
                    .catch((x) => {
                        // Remote version info is not available (offline mode), so just say we're up-to-date
                        this._status = ClientStatus.Available;
                    });
            })
            .catch((x) => {
                this._status = ClientStatus.NotAvailable;
            });
    }

    // true if freeSpace < 10 GB/5 GB. Gets checked every 60 seconds by refreshIntervalHandle and by opening the Acquisition Service Module
    public freeSpaceCheck(): boolean {
        // 10 GB
        if (this.storageStatus && this.storageStatus.freeSpace < ClientService.freeSpaceLimit) {
            if (!(this.freeSpaceStatus > FreeSpaceStatus.EnoughFreeSpace)) {
                // 5 GB
                if (
                    !(this.freeSpaceStatus > FreeSpaceStatus.CriticalFreeSpace) &&
                    this.storageStatus.freeSpace < ClientService.freeSpaceLimit - 5368709120
                ) {
                    this.notificationService.registerProgressNotification(
                        "Not enough free space available",
                        "You have less than 5 GB of free space on your local device. Please change the storage path or delete local data",
                        NotificationUrgency.Warning
                    );

                    this._freeSpaceStatus = FreeSpaceStatus.OutOfFreeSpace;
                } else {
                    this.notificationService.registerProgressNotification(
                        "Running out of free space",
                        "You have less than 10 GB of free space on your local device. It's advised to change the storage path or delete local data",
                        NotificationUrgency.Info
                    );

                    this._freeSpaceStatus = FreeSpaceStatus.CriticalFreeSpace;
                }
            }
            return true;
        }
        this._freeSpaceStatus = FreeSpaceStatus.EnoughFreeSpace;
        return false;
    }
    //#endregion
}
